import { Fragment } from "react";
import { drugTypes, reasonOptions } from "../../patient/clinical-services/service-steps/clinical-assessment/data";
import {
  Section,
  H3,
  CheckInput,
} from "./common-components";
import {
  capitalizeFirstLetter,
  formatToYYYY_MMM_DD,
} from "../../../helpers/functions";
import {
  AdherenceTypes,
  AmrDisplayKind,
  formatAmrForDisplay,
  OverrideReasonOptions,
  WhoOptions,
  WhyOptions,
  normalizeMedicationTypeTherapy,
} from "../../patient/clinical-services/service-steps/medication/data";
import { GetDropDownValue } from "./data";

const PRESCRIPTION_TITLE =
  "Medications - Prescriptions (Prescriptions filled at the current pharmacy)";
const ADDITIONAL_TITLE =
  "Medications - Additional Medications (Non-Prescriptions, Samples, Study Drugs, Natural Products or Prescriptions filled at alternate pharmacies)";

/** Between consecutive medication rows — dashed teal rule (AMR worksheet) so each new drug reads as a distinct block. */
function MedicationEntryDivider() {
  return (
    <hr
      className="my-4 w-full border-0 border-t-2 border-dashed border-teal-300"
      aria-hidden="true"
    />
  );
}

function getAdherenceLabel(type) {
  switch (type) {
    case AdherenceTypes.AsPrescribed:
      return "Taken as prescribed";
    case AdherenceTypes.NotAsPrescribed:
      return "Not taken as prescribed";
    case AdherenceTypes.Stopped:
      return "Stopped taking medication";
    default:
      return "";
  }
}

/** Read-only Yes/No row for AMR print layout (Figma 1141:3350). */
function AmrAdherenceStatusCheckboxes({ adherence }) {
  const yes = adherence === AdherenceTypes.AsPrescribed;
  const no =
    adherence === AdherenceTypes.NotAsPrescribed || adherence === AdherenceTypes.Stopped;
  return (
    <div className="flex flex-wrap items-start justify-end gap-3">
      <span className="text-xs font-normal text-gray-800">Adherence Status</span>
      <div className="flex items-center gap-3">
        <CheckInput checked={yes} label="Yes" />
        <CheckInput checked={no} label="No" />
      </div>
    </div>
  );
}

function hasCreateInterventionDetails(intervention) {
  if (!intervention) return false;
  return (
    intervention.interventionType != null ||
    intervention.drugTherapyProblem != null ||
    (Array.isArray(intervention.actionPlan) && intervention.actionPlan.length > 0) ||
    intervention.urgencyLevel != null
  );
}

function hasInterventionAssessmentDetails(intervention) {
  if (!intervention) return false;
  return (
    intervention.medicationName != null ||
    intervention.whoChangedMedication != null ||
    intervention.whyChangedMedication != null ||
    intervention.whenChangedMedication != null ||
    intervention.override != null
  );
}

function formatHowTaking(intervention) {
  const parts = [];
  if (intervention.medicationName) parts.push(intervention.medicationName);
  const dose = [intervention.dosage, intervention.form, intervention.route, intervention.frequency]
    .filter(Boolean)
    .join(" ");
  if (dose) parts.push(dose);
  if (intervention.prn) parts.push("PRN");
  if (intervention.sig) parts.push(intervention.sig);
  return parts.join(" – ") || "";
}

function formatActionPlanDisplay(actionPlan, specifyOther) {
  if (!Array.isArray(actionPlan) || actionPlan.length === 0) return "";
  const labels = actionPlan.map((a) => (typeof a === "string" ? a : a?.label ?? "")).filter(Boolean);
  let text = labels.join(", ");
  if (specifyOther && (labels.some((l) => (l || "").toLowerCase().includes("other")) || labels.includes("Other"))) {
    text = text ? `${text} – ${specifyOther}` : specifyOther;
  }
  return text;
}

function formatReasonText(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";

  return text
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/** AMR form template: format change date as YYYY-MMM-DD when ISO date. */
function formatInterventionWhenDisplay(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return formatToYYYY_MMM_DD(s) || s;
  return s;
}

function interventionDoseFormRouteFrequency(inv) {
  if (!inv) return "";
  const parts = [inv.dosage, inv.form, inv.route, inv.frequency].filter(Boolean);
  if (inv.prn) parts.push("PRN");
  return parts.join(" ");
}

/** Category segment from AMR `type|therapy` (e.g. Prescription|Chronic → Prescription). */
function getMedicationTypeCategoryLabel(med, review) {
  const stored = med?.medicationType ?? review?.medicationType;
  if (stored != null && String(stored).includes("|")) {
    const type = String(normalizeMedicationTypeTherapy(stored)).split("|")[0]?.trim();
    return formatReasonText(type || "");
  }
  if (stored != null && String(stored).trim() !== "") {
    return formatReasonText(String(stored).trim());
  }
  return formatReasonText((med?.type ?? med?.name ?? med?.displayName ?? "").toString());
}

/** Therapy segment from AMR `type|therapy` — print layout matches worksheet wording. */
function getTherapyTypeLabel(storedMedicationType) {
  const normalized = normalizeMedicationTypeTherapy(storedMedicationType);
  const therapy = String(normalized).split("|")[1]?.trim() ?? "";
  if (therapy.toLowerCase() === "chronic") return "Chronic/ Continuous";
  if (therapy.toLowerCase() === "acute") return "Acute/ short-term";
  return "";
}

/** Matches medication review multi-select storage in `medicationReviewCard.jsx`. */
const SPECIAL_INSTRUCTIONS_STORE_SEP = " ||| ";

function formatSpecialInstructionsForDisplay(value) {
  if (value == null || String(value).trim() === "") return "";
  const s = String(value).trim();
  return s.includes(SPECIAL_INSTRUCTIONS_STORE_SEP)
    ? s.split(SPECIAL_INSTRUCTIONS_STORE_SEP).join("; ")
    : s;
}

export const Medications = ({ formType, medications, patientMedications }) => {
  const allReviews =
    medications?.medications?.map((review) => {
      const med = patientMedications?.find((x) => x.id === review.medicationId);
      return { review, med };
    }) || [];

  const isAdditional = (x) => x.review.drugType === drugTypes.Additional || x.med?.medicationType === drugTypes.Additional;
  const prescriptionReviews = allReviews.filter((x) => !isAdditional(x));
  const additionalReviews = allReviews.filter(isAdditional);

  function renderMedicationCard(x) {
    const { review, med } = x;
    const prn = med?.PRN ?? med?.prn ?? false;
    const rawReasonValue = prn
      ? GetDropDownValue(reasonOptions, med?.prnReason ?? review?.prnReason) || med?.prnReason || review?.prnReason
      : GetDropDownValue(reasonOptions, med?.reason ?? med?.reasonsForMedicationGenCode ?? review?.reasonForMedicationCode) ||
        med?.reasonForMedicationName ||
        review?.reasonForMedication;
    const reasonValue = formatReasonText(rawReasonValue);

    const adherence = review.adherence ?? review.consumption;
    const specialInstructionsRaw =
      review.specialIntructions ??
      med?.specialIntructions ??
      review.specialInstructions ??
      med?.specialInstructions;
    const specialInstructionsDisplay =
      formatSpecialInstructionsForDisplay(specialInstructionsRaw);
    const inv = review.intervention;
    const showCreateInv = hasCreateInterventionDetails(inv);
    const showAssessmentInv = hasInterventionAssessmentDetails(inv);

    const medicationNameDisplay = med?.medicationName ?? med?.name ?? med?.displayName ?? "";
    const reasonDisplay = prn
      ? (reasonValue ? `PRN ${reasonValue}` : `PRN ${med?.prnReason ?? review?.prnReason ?? ""}`)
      : (reasonValue ?? "");

    return (
      <div className="space-y-4">
          {/* 1. Medication details - labels above table, then table */}
          <div className="w-full space-y-1">
            {/* Name of Medication & Reason on top of table - medium bold, with border and titles */}
            <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-10 gap-y-1 bg-gray-20 px-1 py-1 text-xs font-semibold text-teal-300">
              <span className="min-w-0 break-words">
                <span className="font-normal">Name of Medication - </span>
                {medicationNameDisplay}
              </span>
              <span className="shrink-0 text-left">
                <span className="font-normal">Medication Type - </span>
                {getMedicationTypeCategoryLabel(med, review)}
              </span>
              <span className="min-w-0 break-words">
                <span className="font-normal">Reason / PRN Reason - </span>
                {reasonDisplay}
              </span>
              <span className="shrink-0 text-left">
                <span className="font-normal">Therapy Type - </span>
                {getTherapyTypeLabel(med?.medicationType ?? review?.medicationType)}
              </span>
            </div>
            <table className="text-xs border border-t-0 border-gray-400 border-separate border-spacing-0 w-full rounded-b-md table-auto">
              <thead>
                <tr className="text-left bg-gray-20 border-b border-gray-400">
                  <th className="border border-gray-400 font-normal p-1">Dosage, Form, Route</th>
                  <th className="border border-gray-400 font-normal p-1">Frequency/PRN</th>
                  <th className="border border-gray-400 font-normal p-1">SIG (Directions)</th>
                  <th className="border border-REELTeal font-normal p-1">Duration/Units</th>
                  <th className="border border-REELTeal font-normal p-1">Start date</th>
                  <th className="border border-REELTeal font-normal p-1">End Date</th>
                </tr>
              </thead>
              <tbody>
                <tr className="font-semibold">
                  <td className="border border-gray-400 p-1">
                    {[med?.dosage, med?.form, med?.route].filter(Boolean).join(" ") || ""}
                  </td>
                  <td className="border border-gray-400 p-1">
                    {prn
                      ? [med?.frequency, (reasonValue ? `PRN ${reasonValue}` : `PRN ${med?.prnReason ?? review?.prnReason ?? ""}`)].filter(Boolean).join(" ")
                      : (med?.frequency ?? "")}
                  </td>
                  <td className="border border-gray-400 p-1">{med?.sig ?? ""}</td>
                  <td className="border border-REELTeal p-1">
                    {med?.duration && med?.unit ? `${med.duration} ${med.unit}` : (med?.duration ?? "")}
                  </td>
                  <td className="border border-REELTeal p-1">{med?.duration ? (formatToYYYY_MMM_DD(med?.startDate) ?? "") : ""}</td>
                  <td className="border border-REELTeal p-1">{med?.duration ? (formatToYYYY_MMM_DD(med?.endDate) ?? "") : ""}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 2. Assessment Results - Patient Comments — Figma 1141:3350 */}
          <div className="flex flex-col gap-2 rounded-[3px] border border-teal-100 bg-gray-10 p-2">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-1.5">
              <p className="text-sm font-semibold text-teal-300 whitespace-nowrap">
                Assessment Results - Patient Comments
              </p>
              <AmrAdherenceStatusCheckboxes adherence={adherence} />
            </div>
            <div className="flex flex-col gap-2 text-xs leading-normal text-teal-300">
              <div className="flex flex-wrap items-baseline gap-1">
                <span className="font-normal">Adherence - How are you taking the med?</span>
                <span className="font-semibold">{getAdherenceLabel(adherence)}</span>
              </div>
              <div className="flex flex-wrap items-baseline gap-1">
                <span className="font-normal">Effectiveness - Is the medication working?</span>
                <span className="font-semibold">
                  {capitalizeFirstLetter(review.effectiveness ?? review.working)}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2 border-b border-gray-100 pb-2 text-xs leading-normal text-teal-300">
              <div className="flex flex-wrap items-baseline gap-1">
                <span className="font-normal">Safety - Are there any side effects?</span>
                <span className="font-semibold">{capitalizeFirstLetter(review.safety ?? review.safe)}</span>
              </div>
              <div className="flex flex-wrap items-baseline gap-1">
                <span className="font-normal">Proper Technique</span>
                <span className="font-semibold">
                  {capitalizeFirstLetter(review.properTechnique ?? review.technique) || "Not applicable"}
                </span>
              </div>
            </div>
            {specialInstructionsDisplay ? (
              <div className="flex flex-wrap items-baseline gap-1  pb-2 text-xs leading-normal text-teal-300">
                <span className="font-normal">Special Instructions</span>
                <span className="font-semibold min-w-0 break-words">
                  {specialInstructionsDisplay}
                </span>
              </div>
            ) : null}
            <div className="flex flex-wrap items-baseline gap-1 text-xs leading-normal text-teal-300">
              <span className="font-normal">Pharmacist Assessment Comments</span>
              <span className="font-semibold min-w-0 break-words">
                {review.comments ?? review.comment ?? ""}
              </span>
            </div>
          </div>

          {/* 3. Intervention + medication intervention details — AMR form template (Figma 1141:3369) */}
          {(showAssessmentInv || showCreateInv) && review.intervention && (
            <div className="flex flex-col gap-2 rounded-[3px] border border-gray-100 bg-white p-2 text-xs leading-normal text-gray-800">
              {showAssessmentInv && (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 border-b border-gray-400 pb-1">
                    <p className="text-sm font-semibold">Intervention Details - Assessment</p>
                    <div className="flex min-w-0 flex-wrap items-baseline justify-end gap-1 text-right">
                      <span className="font-normal">Adherence Status</span>
                      <span className="font-semibold">{getAdherenceLabel(adherence)}</span>
                    </div>
                  </div>
                  <p className="min-w-0 break-words">
                    <span className="font-normal">Medication Name - </span>
                    <span className="font-semibold">
                      {inv.medicationName ?? medicationNameDisplay ?? ""}
                    </span>
                  </p>
                  <p className="font-semibold">Medication Change Summary</p>
                  <div className="flex flex-col gap-2">
                    <p className="min-w-0 break-words">
                      <span className="font-normal">Who initiated the change? </span>
                      <span className="font-semibold">
                        {GetDropDownValue(WhoOptions, inv.whoChangedMedication) ||
                          capitalizeFirstLetter(inv.whoChangedMedication ?? "")}
                      </span>
                    </p>
                    <p className="min-w-0 break-words">
                      <span className="font-normal">Why did the change occur? </span>
                      <span className="font-semibold">
                        {GetDropDownValue(WhyOptions, inv.whyChangedMedication) ||
                          capitalizeFirstLetter(inv.whyChangedMedication ?? "")}
                      </span>
                    </p>
                  </div>
                  <div className="border-b border-gray-100 pb-2">
                    <p className="min-w-0 break-words">
                      <span className="font-normal">When did the change occur? </span>
                      <span className="font-semibold">
                        {formatInterventionWhenDisplay(inv.whenChangedMedication ?? inv.whenChangedDate)}
                      </span>
                    </p>
                  </div>
                  <p className="font-semibold">How are you taking the medication?</p>
                  <p className="min-w-0 break-words">
                    <span className="font-normal">Medication Name - </span>
                    <span className="font-semibold">
                      {inv.medicationName ?? medicationNameDisplay ?? ""}
                    </span>
                  </p>
                  <p className="min-w-0 break-words font-semibold">
                    {interventionDoseFormRouteFrequency(inv) || "—"}
                  </p>
                  <div className="border-b border-gray-100 pb-2">
                    <p className="min-w-0 break-words font-semibold">Sig or Instructions</p>
                    <p className="mt-1 min-w-0 break-words font-normal">{inv.sig ?? ""}</p>
                  </div>
                  <div className="flex flex-col gap-1 rounded-sm bg-gray-10 px-2 py-2">
                    <p className="font-semibold">Override Reason</p>
                    <p className="min-w-0 break-words">
                      <span className="font-normal">Pharmacist Clinical Decision: </span>
                      <span className="font-semibold">
                        {GetDropDownValue(OverrideReasonOptions, inv.override) ?? ""}
                      </span>
                    </p>
                    <div className="border-b border-gray-100 pb-2">
                      <p className="font-normal">Explanation (Optional)</p>
                      {inv.overrideReason ? (
                        <p className="mt-1 min-w-0 break-words font-semibold">{inv.overrideReason}</p>
                      ) : null}
                    </div>
                  </div>
                </>
              )}

              {showCreateInv && (
                <>
                  <p className="text-sm font-semibold">Medication Intervention - Details</p>
                  <p className="min-w-0 break-words">
                    <span className="font-normal">Intervention Type: </span>
                    <span className="font-semibold">{inv.interventionType ?? ""}</span>
                  </p>
                  <p className="min-w-0 break-words">
                    <span className="font-normal">Original Rx Name - </span>
                    <span className="font-semibold">
                      {([med?.medicationName, med?.route, med?.frequency].filter(Boolean).join(" ") ||
                        inv.medicationName) ??
                        ""}
                    </span>
                  </p>
                  <p className="min-w-0 break-words">
                    <span className="font-normal">Currently Taking - </span>
                    <span className="font-semibold">
                      {formatHowTaking(inv) ||
                        (med
                          ? [med.medicationName, med.dosage, med.form, med.route, med.frequency].filter(Boolean).join(" ")
                          : "")}
                    </span>
                  </p>
                  <p className="min-w-0 break-words">
                    <span className="font-normal">Drug Therapy Problem - </span>
                    <span className="font-semibold">{inv.drugTherapyProblem ?? ""}</span>
                  </p>
                  <p className="min-w-0 break-words">
                    <span className="font-normal">Action Plan </span>
                    <span className="font-semibold">
                      {formatActionPlanDisplay(inv.actionPlan, inv.actionPlanSpecifyOther ?? inv.specifyOther)}
                    </span>
                  </p>
                  {Array.isArray(inv.actionPlan) &&
                    inv.actionPlan.some((a) => (typeof a === "string" ? a : a?.label) === "Other") &&
                    (inv.actionPlanSpecifyOther ?? inv.specifyOther) && (
                      <p className="min-w-0 break-words">
                        <span className="font-normal">Specify Other </span>
                        <span className="font-semibold">{inv.actionPlanSpecifyOther ?? inv.specifyOther}</span>
                      </p>
                    )}
                  <p className="min-w-0 break-words">
                    <span className="font-normal">Urgency Level </span>
                    <span className="font-semibold">
                      {inv.urgencyLevel ? capitalizeFirstLetter(String(inv.urgencyLevel)) : ""}
                    </span>
                  </p>
                  <p className="min-w-0 break-words">
                    <span className="font-normal">Pharmacist Comments </span>
                    <span className="font-semibold">{inv.pharmacistComments ?? ""}</span>
                  </p>
                </>
              )}
            </div>
          )}
        </div>
    );
  }

  return (
    <div className="border-2 border-gray-800 text-gray-800 rounded-lg overflow-hidden">
      <Section className="space-y-1" formType={formType}>

        {prescriptionReviews.length > 0 && (
          <>
            <H3 className="text-teal-300 font-semibold text-base mb-2">{PRESCRIPTION_TITLE}</H3>
            {prescriptionReviews.map((x, i) => (
              <Fragment key={x.review.medicationId ?? i}>
                {renderMedicationCard(x)}
                {i < prescriptionReviews.length - 1 && <MedicationEntryDivider />}
              </Fragment>
            ))}
          </>
        )}

        {additionalReviews.length > 0 && (
          <div
            className={`w-full ${prescriptionReviews.length > 0 ? "pt-4 mt-4 border-t-2 border-teal-300" : ""}`}
          >
            <H3 className="text-teal-300 font-semibold text-base mb-2">
              {ADDITIONAL_TITLE}
            </H3>
            {additionalReviews.map((x, i) => (
              <Fragment key={x.review.medicationId ?? i}>
                {renderMedicationCard(x)}
                {i < additionalReviews.length - 1 && <MedicationEntryDivider />}
              </Fragment>
            ))}
          </div>
        )}

        {allReviews.length === 0 && (
          <p className="text-sm text-gray-500">No medications reviewed.</p>
        )}
      </Section>
    </div>
  );
};
